import React, { createContext, useContext, useState, useCallback } from 'react';

// Membuat context untuk state form
const FormContext = createContext();

// Provider component untuk FormContext
export function FormProvider({ children, initialValues = {}, onSubmit }) {
    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState(initialValues);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // null, 'success', 'error'
    const [submitMessage, setSubmitMessage] = useState('');

    // Array untuk menyimpan referensi ke semua step
    const [steps, setSteps] = useState([]);

    // Fungsi untuk mendaftarkan setiap step form
    const registerStep = useCallback((stepId) => {
        setSteps(prevSteps => {
            if (!prevSteps.includes(stepId)) {
                return [...prevSteps, stepId];
            }
            return prevSteps;
        });
    }, []);

    // Pindah ke step selanjutnya
    const goToNextStep = useCallback(() => {
        if (activeStep < steps.length - 1) {
            setActiveStep(activeStep + 1);
            return true;
        }
        return false;
    }, [activeStep, steps.length]);

    // Pindah ke step sebelumnya
    const goToPrevStep = useCallback(() => {
        if (activeStep > 0) {
            setActiveStep(activeStep - 1);
            return true;
        }
        return false;
    }, [activeStep]);

    // Pindah ke step tertentu
    const goToStep = useCallback((stepIndex) => {
        if (stepIndex >= 0 && stepIndex < steps.length) {
            setActiveStep(stepIndex);
            return true;
        }
        return false;
    }, [steps.length]);

    // Update data form
    const updateFormData = useCallback((newData) => {
        setFormData(prevData => ({
            ...prevData,
            ...newData
        }));
    }, []);

    // Update field tunggal
    const updateField = useCallback((name, value) => {
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));

        // Hapus error untuk field ini jika ada
        if (errors[name]) {
            setErrors(prevErrors => {
                const newErrors = { ...prevErrors };
                delete newErrors[name];
                return newErrors;
            });
        }
    }, [errors]);

    // Handle input change
    const handleChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        const inputValue = type === 'checkbox' ? checked : value;
        updateField(name, inputValue);
    }, [updateField]);

    // Set errors
    const setFieldErrors = useCallback((newErrors) => {
        setErrors(prevErrors => ({
            ...prevErrors,
            ...newErrors
        }));
    }, []);

    // Reset errors
    const resetErrors = useCallback(() => {
        setErrors({});
    }, []);

    // Submit form
    const submitForm = useCallback(async () => {
        try {
            setIsSubmitting(true);
            setSubmitStatus(null);
            setSubmitMessage('');
            
            // Panggil handler onSubmit jika disediakan
            if (onSubmit) {
                await onSubmit(formData);
            }
            
            setSubmitStatus('success');
            setSubmitMessage('Data berhasil disimpan!');
            return true;
        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitStatus('error');
            setSubmitMessage(error.message || 'Terjadi kesalahan saat menyimpan data.');
            return false;
        } finally {
            setIsSubmitting(false);
        }
    }, [formData, onSubmit]);

    // Reset form
    const resetForm = useCallback(() => {
        setFormData(initialValues);
        setErrors({});
        setActiveStep(0);
        setSubmitStatus(null);
        setSubmitMessage('');
    }, [initialValues]);

    // Nilai dan fungsi yang akan disediakan oleh context
    const value = {
        // State
        formData,
        errors,
        activeStep,
        steps,
        isSubmitting,
        submitStatus,
        submitMessage,
        
        // Form Actions
        registerStep,
        goToNextStep,
        goToPrevStep,
        goToStep,
        updateFormData,
        updateField,
        handleChange,
        setFieldErrors,
        resetErrors,
        submitForm,
        resetForm
    };

    return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
}

// Custom hook untuk mengakses context
export function useForm() {
    const context = useContext(FormContext);
    if (context === undefined) {
        throw new Error('useForm must be used within a FormProvider');
    }
    return context;
}

export default FormContext;